import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Button,
  Card,
  Col,
  DatePicker,
  Form,
  Input,
  InputNumber,
  message,
  Modal,
  Row,
  Select,
  Table,
  Tag,
  Tooltip,
  Upload,
} from "antd";
import { PlusOutlined, UploadOutlined, QuestionCircleOutlined } from "@ant-design/icons";
import GuidedTour, { hasTourFlag } from "../common/GuidedTour/GuidedTour";
import moment from "moment";
import Service from "../../service";
import { useDispatch, useSelector } from "react-redux";
import { hideAuthLoader, showAuthLoader } from "../../appRedux/actions/Auth";
import {
  ACCEPTED_FILE_TYPES,
  expenseApi,
  formatMoney,
  getExpenseCurrencySymbol,
  setExpenseCurrencySymbol,
  MAX_ATTACHMENTS,
  statusTag,
  validateAttachment,
} from "./expenseHelpers";
import { getResource } from "../../util/getResourcePermission";
import { PAGE_SIZE_OPTIONS } from "../../constants/tablePagination";
import config from "../../permission/config.json";
import NodataFoundIcon from "../../assets/images/NodataFoundIcon";
import "./expenseManagement.css";
import useCompanyDateFormat from "../../hooks/useCompanyDateFormat";

const { TextArea } = Input;
const { Option } = Select;

const TOUR_STORAGE_KEY = "tour_seen_expense_submission";
const TOUR_HINT_STORAGE_KEY = "tour_hint_dismissed_expense_submission";
// Steps whose target field lives inside the Submit Expense modal - the tour
// opens that real modal itself when it reaches one of these.
const MODAL_STEP_IDS = [
  "es-date",
  "es-category",
  "es-amount",
  "es-payment",
  "es-vendor-bill",
  "es-description",
  "es-attachments",
  "es-submit-btn",
];

// Expense Submission - submit an expense against a selected advance, showing the
// live remaining balance for that advance and surfacing duplicate-bill / exception
// flags returned by the backend.
const ExpenseSubmission = () => {
  const dateFormat = useCompanyDateFormat();
  const dispatch = useDispatch();
  const { authUser } = useSelector(({ auth }) => auth);
  // Logged-in employee id - used to gate the "Resubmit" action to the owner only.
  const currentUserId = authUser?._id;
  const [form] = Form.useForm();
  const [resubmitForm] = Form.useForm();

  const [advances, setAdvances] = useState([]);
  const [expenses, setExpenses] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedAdvance, setSelectedAdvance] = useState(null);
  const [balance, setBalance] = useState(null);
  const [attachments, setAttachments] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [warning, setWarning] = useState(null);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 25 });
  const [total, setTotal] = useState(0);
  // Company currency symbol (from Expense Config); drives input prefixes.
  const [currencySymbol, setCurrencySymbol] = useState(getExpenseCurrencySymbol());

  // Resubmit (after "Request Changes") - owner edits and re-sends the expense.
  const [resubmitTarget, setResubmitTarget] = useState(null);
  const [resubmitAttachments, setResubmitAttachments] = useState([]);
  const [resubmitWarning, setResubmitWarning] = useState(null);

  const mayCreate = useMemo(
    () => getResource(config.RESOURCES.EXPENSE_SUBMISSION, ["create"]),
    []
  );

  const [isTourOpen, setIsTourOpen] = useState(false);
  const [isTourHintOpen, setIsTourHintOpen] = useState(false);

  // First-time visitors get a "click here" nudge pointing at the guide
  // trigger. It stays up until dismissed, which marks it done via its own
  // storage key so it never comes back.
  useEffect(() => {
    if (hasTourFlag(TOUR_STORAGE_KEY) || hasTourFlag(TOUR_HINT_STORAGE_KEY)) return;
    const showTimer = setTimeout(() => setIsTourHintOpen(true), 600);
    return () => clearTimeout(showTimer);
  }, []);

  // Once the tour walks past the "+ Expense" button, it opens the real
  // Submit Expense modal so the remaining steps can point at its actual
  // fields - and closes it again once the tour leaves those steps.
  const handleTourStepChange = useCallback((step) => {
    setIsModalOpen(Boolean(step && MODAL_STEP_IDS.includes(step.id)));
  }, []);

  const loadAdvances = useCallback(async () => {
    dispatch(showAuthLoader());
    try {
      const data = await expenseApi.get(Service.expenseAdvance, {
        page: 1,
        limit: 200,
        status: "Issued",
        // Only the advance owner may submit expenses against it, so list the
        // caller's own advances even for org-wide roles (Admin/HR/Account Team).
        scope: "own",
      });
      setAdvances(data?.items || []);
    } catch (err) {
      message.error(err.message || "Failed to load advances");
    } finally {
      dispatch(hideAuthLoader());
    }
  }, [dispatch]);

  const loadExpenses = useCallback(async () => {
    dispatch(showAuthLoader());
    try {
      const data = await expenseApi.get(Service.expense, {
        page: pagination.current,
        limit: pagination.pageSize,
      });
      setExpenses(data?.items || []);
      setTotal(data?.total || 0);
    } catch (err) {
      message.error(err.message || "Failed to load expenses");
    } finally {
      dispatch(hideAuthLoader());
    }
  }, [dispatch, pagination.current, pagination.pageSize]);

  const loadConfig = useCallback(async () => {
    try {
      const config = await expenseApi.get(Service.expenseConfig);
      setCategories(config?.categories || []);
      const sym = config?.currencySymbol || "₹";
      setExpenseCurrencySymbol(sym);
      setCurrencySymbol(sym);
    } catch {
      /* config is optional for submission */
    }
  }, []);

  useEffect(() => {
    loadAdvances();
    loadExpenses();
    loadConfig();
  }, [loadAdvances, loadExpenses, loadConfig]);

  const loadBalance = useCallback(
    async (advanceId) => {
      if (!advanceId) {
        setBalance(null);
        return;
      }
      dispatch(showAuthLoader());
      try {
        const data = await expenseApi.get(`${Service.expenseBalance}/${advanceId}`);
        setBalance(data);
      } catch (err) {
        message.error(err.message || "Failed to load balance");
      } finally {
        dispatch(hideAuthLoader());
      }
    },
    [dispatch]
  );

  const onSelectAdvance = (advanceId) => {
    setSelectedAdvance(advanceId);
    loadBalance(advanceId);
  };

  const openModal = () => {
    if (!selectedAdvance) {
      message.warning("Select an advance first");
      return;
    }
    form.resetFields();
    form.setFieldsValue({ expenseDate: moment() });
    setAttachments([]);
    setWarning(null);
    setIsModalOpen(true);
  };

  // Open an attachment in a new tab. Newly-picked files (not yet uploaded) are raw
  // File/Blob objects, so build a temporary object URL to preview them.
  const previewAttachment = (file) => {
    const src =
      file.url ||
      (file.originFileObj instanceof Blob && URL.createObjectURL(file.originFileObj)) ||
      (file instanceof Blob && URL.createObjectURL(file)) ||
      null;
    if (src) window.open(src, "_blank", "noopener,noreferrer");
  };

  // Keep the remove + preview icons always visible (not only on hover, see CSS).
  const uploadListConfig = { showRemoveIcon: true, showPreviewIcon: true };

  const uploadProps = {
    multiple: true,
    accept: ACCEPTED_FILE_TYPES,
    fileList: attachments,
    showUploadList: uploadListConfig,
    onPreview: previewAttachment,
    beforeUpload: (file) => {
      if (!validateAttachment(file, attachments)) {
        return Upload.LIST_IGNORE;
      }
      setAttachments((prev) => [...prev, file]);
      return false;
    },
    onRemove: (file) =>
      setAttachments((prev) => prev.filter((f) => f.uid !== file.uid)),
  };

  const handleSubmit = async (values) => {
    dispatch(showAuthLoader());
    setWarning(null);
    try {
      const formData = new FormData();
      formData.append("advanceId", selectedAdvance);
      formData.append("expenseDate", values.expenseDate.format("YYYY-MM-DD"));
      formData.append("category", values.category);
      formData.append("amount", values.amount);
      formData.append("vendorName", values.vendorName || "");
      formData.append("description", values.description || "");
      formData.append("paymentMode", values.paymentMode || "");
      formData.append("billNumber", values.billNumber || "");
      attachments.forEach((file) => formData.append("attachments", file));

      const data = await expenseApi.post(Service.expense, formData);

      const flags = [];
      if (data?.duplicateBillWarning) flags.push("Duplicate bill number detected.");
      if (data?.expense?.isException)
        flags.push(
          `Over-limit${
            data.exceptionPeriod ? ` (${String(data.exceptionPeriod).toLowerCase()})` : ""
          } - flagged as exception, requires explicit approval.`
        );
      if (flags.length) {
        setWarning(flags.join(" "));
        // Show the actual reason (the modal closes, so the detail must be in the toast).
        message.warning({ content: flags.join(" "), duration: 6 });
      } else {
        message.success("Expense submitted successfully");
      }

      setIsModalOpen(false);
      loadExpenses();
      loadBalance(selectedAdvance);
    } catch (err) {
      message.error(err.message || "Failed to submit expense");
    } finally {
      dispatch(hideAuthLoader());
    }
  };

  // Latest reviewer comment from a "Request Changes" action, shown on resubmit.
  const latestChangeRequest = (record) => {
    const entries = (record?.approvalHistory || []).filter(
      (e) => e.action === "RequestChanges"
    );
    return entries.length ? entries[entries.length - 1].comment : null;
  };

  const openResubmit = (record) => {
    setResubmitTarget(record);
    setResubmitAttachments([]);
    setResubmitWarning(latestChangeRequest(record));
    resubmitForm.setFieldsValue({
      expenseDate: record.expenseDate ? moment(record.expenseDate) : null,
      category: record.category,
      amount: record.amount,
      vendorName: record.vendorName,
      description: record.description,
      paymentMode: record.paymentMode,
      billNumber: record.billNumber,
      comment: undefined,
    });
  };

  const resubmitUploadProps = {
    multiple: true,
    accept: ACCEPTED_FILE_TYPES,
    fileList: resubmitAttachments,
    showUploadList: uploadListConfig,
    onPreview: previewAttachment,
    beforeUpload: (file) => {
      if (!validateAttachment(file, resubmitAttachments)) {
        return Upload.LIST_IGNORE;
      }
      setResubmitAttachments((prev) => [...prev, file]);
      return false;
    },
    onRemove: (file) =>
      setResubmitAttachments((prev) => prev.filter((f) => f.uid !== file.uid)),
  };

  const handleResubmit = async (values) => {
    if (!resubmitTarget) return;
    dispatch(showAuthLoader());
    try {
      const formData = new FormData();
      formData.append("expenseDate", values.expenseDate.format("YYYY-MM-DD"));
      formData.append("category", values.category);
      formData.append("amount", values.amount);
      formData.append("vendorName", values.vendorName || "");
      formData.append("description", values.description || "");
      formData.append("paymentMode", values.paymentMode || "");
      formData.append("billNumber", values.billNumber || "");
      if (values.comment) formData.append("comment", values.comment);
      resubmitAttachments.forEach((file) => formData.append("attachments", file));

      const data = await expenseApi.post(
        `${Service.expense}/${resubmitTarget._id}/resubmit`,
        formData
      );

      if (data?.duplicateBillWarning) {
        message.warning("Expense resubmitted (duplicate bill number detected).");
      } else {
        message.success("Expense resubmitted successfully");
      }
      setResubmitTarget(null);
      loadExpenses();
      loadBalance(selectedAdvance);
    } catch (err) {
      message.error(err.message || "Failed to resubmit expense");
    } finally {
      dispatch(hideAuthLoader());
    }
  };

  const columns = [
    {
      title: "Date",
      dataIndex: "expenseDate",
      key: "expenseDate",
      render: (v) => (v ? moment(v).format(dateFormat) : "-"),
    },
    { title: "Category", dataIndex: "category", key: "category", render: (v) => v || "-" },
    {
      title: "Amount",
      dataIndex: "amount",
      key: "amount",
      render: (v) => formatMoney(v),
    },
    { title: "Vendor", dataIndex: "vendorName", key: "vendorName", render: (v) => v || "-" },
    { title: "Bill No.", dataIndex: "billNumber", key: "billNumber", render: (v) => v || "-" },
    {
      title: "Status",
      dataIndex: "approvalStatus",
      key: "approvalStatus",
      render: (v) => statusTag(v),
    },
    {
      title: <span data-tour="es-actions">Actions</span>,
      key: "actions",
      align: "center",
      // Resubmit is shown only to the employee who submitted the expense - not to
      // admins/managers viewing someone else's expense (the backend enforces this too).
      render: (_, record) =>
        record.approvalStatus === "RequestChanges" &&
        String(record.employeeId) === String(currentUserId) ? (
          <Button type="link" className="edit" onClick={() => openResubmit(record)}>
            Resubmit
          </Button>
        ) : (
          <span className="expense-muted">-</span>
        ),
    },
  ];

  const tourSteps = useMemo(
    () => [
      {
        id: "es-advance",
        target: '[data-tour="es-advance"]',
        title: "Pick an advance first",
        content:
          "Every expense you submit must be linked to an advance you've received. Select it here first - once you do, a live Advance / Submitted / Remaining summary appears next to it.",
      },
      {
        id: "es-add-btn",
        target: '[data-tour="es-add-btn"]',
        title: "Add your expense",
        content: "Click + Expense - it opens a short form. It won't open until you've picked an advance above.",
      },
      {
        id: "es-date",
        target: '[data-tour="es-date"]',
        title: "When it happened",
        content: "The expense date - can't be a future date.",
      },
      {
        id: "es-category",
        target: '[data-tour="es-category"]',
        title: "What kind of expense",
        content: "Pick from your company's category list, or type your own if none are set up.",
      },
      {
        id: "es-amount",
        target: '[data-tour="es-amount"]',
        title: "How much",
        content: "Required. If this pushes you over your category limit, you'll still be able to submit, but it gets flagged for extra approval.",
      },
      {
        id: "es-payment",
        target: '[data-tour="es-payment"]',
        title: "How you paid",
        content: "Optional - Cash, Card, UPI, Bank Transfer or Other.",
      },
      {
        id: "es-vendor-bill",
        target: '[data-tour="es-vendor-bill"]',
        title: "Vendor and bill number",
        content: "Both optional. A duplicate bill number against the same vendor gets flagged with a warning, but it still submits.",
      },
      {
        id: "es-description",
        target: '[data-tour="es-description"]',
        title: "Add a note",
        content: "Optional - any extra context for whoever approves this.",
      },
      {
        id: "es-attachments",
        target: '[data-tour="es-attachments"]',
        title: "Attach your receipt",
        content: `Optional, JPG/PNG/PDF, up to ${MAX_ATTACHMENTS} files.`,
      },
      {
        id: "es-submit-btn",
        target: '[data-tour="es-submit-btn"]',
        title: "Submit",
        content: "Sends this expense for approval against the advance you selected.",
      },
      {
        target: '[data-tour="es-actions"]',
        title: "If changes are requested",
        content:
          "If a reviewer asks for changes, the status shows Request Changes and a Resubmit button appears here for you to fix and resend it.",
      },
    ],
    []
  );

  const hintSteps = useMemo(
    () => [
      {
        target: '[data-tour="es-guide-icon"]',
        shape: "circle",
        title: "New here?",
        content: "Click this icon anytime for a quick, step-by-step guide to this screen.",
      },
    ],
    []
  );

  return (
    <Card className="employee-card expense-page">
      <div className="inout-header text-left">
        <h2>
          Submit Expense{" "}
          <QuestionCircleOutlined
            data-tour="es-guide-icon"
            className="guide-title-icon"
            role="button"
            tabIndex={0}
            onClick={() => {
              setIsTourHintOpen(false);
              setIsTourOpen(true);
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                setIsTourHintOpen(false);
                setIsTourOpen(true);
              }
            }}
            title="Click for a step-by-step guide"
          />
        </h2>
        <div className="header-btns">
          {mayCreate && (
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={openModal}
              data-tour="es-add-btn"
            >
              Expense
            </Button>
          )}
        </div>
      </div>
      <Card className="main-content-wrapper">
        <div className="search-wrapper">
          <div className="expense-advance-selector" data-tour="es-advance">
            <label className="expense-label">Advance</label>
            <Select
              showSearch
              allowClear
              style={{ width: "100%" }}
              placeholder="Select an advance to submit against"
              optionFilterProp="children"
              value={selectedAdvance}
              onChange={onSelectAdvance}
            >
              {advances.map((adv) => (
                <Option key={adv._id} value={adv._id}>
                  {adv.advanceId} - {formatMoney(adv.advanceAmount)}
                </Option>
              ))}
            </Select>
          </div>
          {balance && (
            <div className="expense-balance-widget">
              <div className="expense-balance-stat">
                <span className="expense-balance-label">Advance</span>
                <span className="expense-balance-value">
                  {formatMoney(balance.totalAdvance)}
                </span>
              </div>
              <div className="expense-balance-stat">
                <span className="expense-balance-label">Submitted</span>
                <span className="expense-balance-value">
                  {formatMoney(balance.submitted)}
                </span>
              </div>
              <div className="expense-balance-stat">
                <span className="expense-balance-label">Remaining</span>
                <span
                  className={`expense-balance-value ${
                    balance.remaining < 0
                      ? "expense-balance-negative"
                      : "expense-balance-positive"
                  }`}
                >
                  {formatMoney(balance.remaining)}
                </span>
              </div>
            </div>
          )}
        </div>

        <div className="main-table-wrapper">
          <Table
            rowKey="_id"
            columns={columns}
            dataSource={expenses}
            locale={{ emptyText: <NodataFoundIcon /> }}
            pagination={{
              current: pagination.current,
              pageSize: pagination.pageSize,
              total,
              showSizeChanger: true,
              pageSizeOptions: PAGE_SIZE_OPTIONS,
              onChange: (current, pageSize) => setPagination({ current, pageSize }),
            }}
            scroll={{ x: "max-content" }}
          />
        </div>
      </Card>

      <Modal
        title="Submit Expense"
        className="expense-upload-modal"
        open={isModalOpen}
        onCancel={() => setIsModalOpen(false)}
        onOk={() => form.submit()}
        okText="Submit"
        okButtonProps={{ "data-tour": "es-submit-btn" }}
        destroyOnClose
        width={680}
      >
        {warning && (
          <Alert type="warning" showIcon message={warning} style={{ marginBottom: 12 }} />
        )}
        <Form form={form} layout="vertical" onFinish={handleSubmit}>
          <Row gutter={16}>
            <Col span={12} data-tour="es-date">
              <Form.Item
                name="expenseDate"
                label="Date"
                rules={[{ required: true, message: "Select the expense date" }]}
              >
                <DatePicker
                  style={{ width: "100%" }}
                  format="DD-MM-YYYY"
                  disabledDate={(d) => d && d > moment().endOf("day")}
                />
              </Form.Item>
            </Col>
            <Col span={12} data-tour="es-category">
              <Form.Item
                name="category"
                label="Category"
                rules={[{ required: true, message: "Select a category" }]}
              >
                {categories.length ? (
                  <Select placeholder="Select category">
                    {categories.map((c) => (
                      <Option key={c} value={c}>
                        {c}
                      </Option>
                    ))}
                  </Select>
                ) : (
                  <Input placeholder="Enter category" />
                )}
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={12} data-tour="es-amount">
              <Form.Item
                name="amount"
                label="Amount"
                rules={[{ required: true, message: "Enter the amount" }]}
              >
                <Input type="number" min={0} style={{ width: "100%" }} prefix={<span style={{ marginRight: 4 }}>{currencySymbol}</span>} placeholder="0.00" />
              </Form.Item>
            </Col>
            <Col span={12} data-tour="es-payment">
              <Form.Item name="paymentMode" label="Payment Mode">
                <Select allowClear placeholder="Select payment mode">
                  <Option value="Cash">Cash</Option>
                  <Option value="Card">Card</Option>
                  <Option value="UPI">UPI</Option>
                  <Option value="Bank Transfer">Bank Transfer</Option>
                  <Option value="Other">Other</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16} data-tour="es-vendor-bill">
            <Col span={12}>
              <Form.Item name="vendorName" label="Vendor">
                <Input placeholder="Vendor name" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="billNumber" label="Bill Number">
                <Input placeholder="Bill / invoice number" />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item name="description" label="Description" data-tour="es-description">
            <TextArea rows={2} placeholder="Description" />
          </Form.Item>
          <Form.Item label={`Attachments (JPG/PNG/PDF, max ${MAX_ATTACHMENTS})`} data-tour="es-attachments">
            <Upload {...uploadProps}>
              <Button icon={<UploadOutlined />} disabled={attachments.length >= MAX_ATTACHMENTS}>
                Upload
              </Button>
            </Upload>
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="Resubmit Expense"
        className="expense-upload-modal"
        open={!!resubmitTarget}
        onCancel={() => setResubmitTarget(null)}
        onOk={() => resubmitForm.submit()}
        okText="Resubmit"
        destroyOnClose
        width={680}
      >
        {resubmitWarning && (
          <Alert
            type="info"
            showIcon
            message="Changes requested by approver"
            description={resubmitWarning}
            style={{ marginBottom: 12 }}
          />
        )}
        <Form form={resubmitForm} layout="vertical" onFinish={handleResubmit}>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="expenseDate"
                label="Date"
                rules={[{ required: true, message: "Select the expense date" }]}
              >
                <DatePicker
                  style={{ width: "100%" }}
                  format="DD-MM-YYYY"
                  disabledDate={(d) => d && d > moment().endOf("day")}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="category"
                label="Category"
                rules={[{ required: true, message: "Select a category" }]}
              >
                {categories.length ? (
                  <Select placeholder="Select category">
                    {categories.map((c) => (
                      <Option key={c} value={c}>
                        {c}
                      </Option>
                    ))}
                  </Select>
                ) : (
                  <Input placeholder="Enter category" />
                )}
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="amount"
                label="Amount"
                rules={[{ required: true, message: "Enter the amount" }]}
              >
                <InputNumber min={0} style={{ width: "100%" }} prefix={<span style={{ marginRight: 4 }}>{currencySymbol}</span>} placeholder="0.00" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="paymentMode" label="Payment Mode">
                <Select allowClear placeholder="Select payment mode">
                  <Option value="Cash">Cash</Option>
                  <Option value="Card">Card</Option>
                  <Option value="UPI">UPI</Option>
                  <Option value="Bank Transfer">Bank Transfer</Option>
                  <Option value="Other">Other</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="vendorName" label="Vendor">
                <Input placeholder="Vendor name" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="billNumber" label="Bill Number">
                <Input placeholder="Bill / invoice number" />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item name="description" label="Description">
            <TextArea rows={2} placeholder="Description" />
          </Form.Item>
          <Form.Item name="comment" label="Comment to approver (optional)">
            <TextArea rows={2} placeholder="Describe what you changed" />
          </Form.Item>
          <Form.Item label={`Add attachments (JPG/PNG/PDF, max ${MAX_ATTACHMENTS})`}>
            <Upload {...resubmitUploadProps}>
              <Button
                icon={<UploadOutlined />}
                disabled={resubmitAttachments.length >= MAX_ATTACHMENTS}
              >
                Upload
              </Button>
            </Upload>
          </Form.Item>
        </Form>
      </Modal>

      {isTourOpen && (
        <GuidedTour
          steps={tourSteps}
          onClose={() => setIsTourOpen(false)}
          onStepChange={handleTourStepChange}
          storageKey={TOUR_STORAGE_KEY}
        />
      )}

      {!isTourOpen && isTourHintOpen && (
        <GuidedTour steps={hintSteps} onClose={() => setIsTourHintOpen(false)} storageKey={TOUR_HINT_STORAGE_KEY} />
      )}
    </Card>
  );
};

export default ExpenseSubmission;
